"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { tokenManager } from "@/lib/token-manager"

// Local storage key for custom token
const CUSTOM_TOKEN_KEY = "fbo_custom_token"

type AuthContextType = {
  token: string | null
  setToken: (token: string | null) => void
  isAuthenticated: boolean
  tokenSource: "url" | "parent" | "custom" | "none"
  saveCustomToken: (token: string) => void
  clearCustomToken: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [tokenSource, setTokenSource] = useState<"url" | "parent" | "custom" | "none">("none")

  // Wrapper function to update token and notify token manager
  const setToken = (newToken: string | null) => {
    console.log("AuthContext: Setting token, source:", tokenSource)
    setTokenState(newToken)
    tokenManager.notifyTokenUpdate(newToken)
  }

  // Save a custom token to localStorage
  const saveCustomToken = (customToken: string) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(CUSTOM_TOKEN_KEY, customToken)
      setToken(customToken)
      setTokenSource("custom")
    }
  }

  // Clear the custom token from localStorage
  const clearCustomToken = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(CUSTOM_TOKEN_KEY)

      // Set token to null instead of reverting to default
      setToken(null)
      setTokenSource("none")
    }
  }

  // Parse URL parameters on initial load to extract the token
  useEffect(() => {
    const extractTokenFromUrl = () => {
      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search)
        const tokenFromUrl = urlParams.get("token")

        if (tokenFromUrl) {
          setToken(tokenFromUrl)
          setTokenSource("url")
          return { token: tokenFromUrl, source: "url" as const }
        }

        // Check for a saved custom token
        const savedToken = localStorage.getItem(CUSTOM_TOKEN_KEY)
        if (savedToken) {
          setToken(savedToken)
          setTokenSource("custom")
          return { token: savedToken, source: "custom" as const }
        }

        // No token available - set to null
        setToken(null)
        setTokenSource("none")
        return { token: null, source: "none" as const }
      }
      return { token: null, source: "none" as const }
    }

    const { token } = extractTokenFromUrl()
    setIsAuthenticated(!!token)
  }, [])

  // Listen for messages from parent window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Process messages from parent window
      try {
        const data = event.data

        // Check if this is a SET_TOKEN message
        if (data && data.action === "SET_TOKEN" && typeof data.token === "string") {
          console.log("Received SET_TOKEN message from parent window")

          // Set the token from the parent window
          setToken(data.token)
          setTokenSource("parent")

          // Optionally save to localStorage if you want it to persist
          // localStorage.setItem(CUSTOM_TOKEN_KEY, data.token)
        }
      } catch (error) {
        console.error("Error processing message from parent:", error)
      }
    }

    // Add event listener for messages
    if (typeof window !== "undefined") {
      window.addEventListener("message", handleMessage)
    }

    // Clean up event listener
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("message", handleMessage)
      }
    }
  }, [])

  // Update authentication status when token changes
  useEffect(() => {
    setIsAuthenticated(!!token)
  }, [token])

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        isAuthenticated,
        tokenSource,
        saveCustomToken,
        clearCustomToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
